import { Box } from '@mui/material'
import { InfoIcon } from '@phosphor-icons/react'

import { Actions } from '@components/shared/Actions'
import { BudgetPreviewsStatus } from '@components/shared/BudgetPreviewsStatus'
import { EditIcon } from '@assets/svgs/EditIcon'
import { emotionTheme } from '@styles/theme'
import { formattedIsoToPtBrDateTime } from '@utils/dates/formattedIsoToPtBrDateTime'
import { TooltipExplanation } from '@components/shared/TooltipExplanation/inde'
import { TrendingDown } from '@assets/svgs/TrendingDown'
import { TrendingUp } from '@assets/svgs/TrendingUp'

import type { BonusPreviewProps } from '../context/types'
import type { ColumnDefProps } from '@components/shared/Table/types'

interface ParseBonusPreviewsGridProps {
  data: BonusPreviewProps[]
  onEdit: (data: BonusPreviewProps) => void
  actionsDisabled: boolean
}

export const parseBonusPreviewsGrid = ({ data, actionsDisabled, onEdit }: ParseBonusPreviewsGridProps) => {
  if (!data) return []

  const parsedData: ColumnDefProps[][] = data.map((item) => [
    {
      id: 'description',
      columnName: 'Descrição',
      minWidth: '7rem',
      align: 'center',
      value: item.previewNumber ? `${item.previewNumber}ª semana` : '-'
    },
    {
      id: 'consumptionTotal',
      columnName: 'Total Consumo (R$)',
      minWidth: '10rem',
      align: 'center',
      currency: true,
      value: item.previewConsumptionAmount || 0
    },
    {
      id: 'totalBonification',
      columnName: 'Total Bonificação (R$)',
      align: 'center',
      currency: true,
      value: item.previewBonusAmount || 0
    },
    {
      id: 'bonificationPercentage',
      columnName: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          Média Bonif. (%)
          <TooltipExplanation
            data={[
              {
                label: 'Média Bonif. (%) = ',
                text: '(Bonificação 3M ÷ Consumo 3M) × 100 (Apresentado em percentual, calculado com base em valores absolutos dos últimos 3 meses)'
              }
            ]}
          >
            <InfoIcon size={18} />
          </TooltipExplanation>
        </Box>
      ),
      minWidth: '11rem',
      align: 'center',
      value: item.previewPercentage ? `${item.previewPercentage.toFixed(2)}%`.replace('.', ',') : '-'
    },
    {
      id: 'deviation',
      columnName: 'Desvio (%)',
      align: 'center',
      minWidth: '8rem',
      value: (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            color: item.deviationPercentage <= 0 ? emotionTheme.colors.error.color : emotionTheme.colors.success.color
          }}
        >
          {item.deviationPercentage <= 0 ? <TrendingDown size="small" /> : <TrendingUp size="small" />}
          {item.deviationPercentage ? `${item.deviationPercentage.toFixed(2)}%`.replace('.', ',') : '-'}
        </Box>
      )
    },
    {
      id: 'adjustment',
      columnName: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          Ajuste (%)
          <TooltipExplanation
            data={[
              {
                label: '',
                text: 'Ao informar o Ajuste (%), esse valor substituirá a Média Bonif. (%) no cálculo da Bonificação. Para que a média seja considerada, o campo de Ajuste (%) deve permanecer em branco.'
              }
            ]}
          >
            <InfoIcon size={18} />
          </TooltipExplanation>
        </Box>
      ),
      align: 'center',
      minWidth: '8rem',
      value:
        item.adjustmentPercentage === 0
          ? '0,00%'
          : item.adjustmentPercentage
            ? `${item.adjustmentPercentage.toFixed(2)}%`.replace('.', ',')
            : '-'
    },
    {
      id: 'observations',
      columnName: 'Observações',
      align: 'center',
      minWidth: '12rem',
      value: item.observation || '-'
    },
    {
      id: 'lastUpdate',
      columnName: 'Última Atualização',
      align: 'center',
      minWidth: '12rem',
      value: item.updatedAt ? formattedIsoToPtBrDateTime(item.updatedAt) : '-'
    },
    {
      id: 'status',
      columnName: 'Status da Prévia',
      align: 'center',
      minWidth: '10rem',
      maxWidth: '14.5rem',
      withoutTooltip: true,
      value: <BudgetPreviewsStatus status={item.previewStatus} />
    },
    {
      id: 'action',
      columnName: 'Ações',
      align: 'center',
      minWidth: '5rem',
      maxWidth: '5rem',
      stickyPosition: 'right',
      withoutTooltip: true,
      value: (
        <Actions
          actions={[{ label: 'Editar', icon: <EditIcon />, onClick: () => onEdit(item) }]}
          disabled={!item.allowEdit || actionsDisabled}
        />
      )
    }
  ])

  return parsedData
}
